import { useState } from 'react';
import styled from '@emotion/styled';
import Form from 'src/components/form/Form';
import { Icon, Input, Message, Select } from 'semantic-ui-react';
import { errorInput, formInput, formLabel, formSelectService } from 'src/components/helpers';
import { screenSizes } from 'src/assets/styles/Literals';

const Container = styled.div`
  max-width: 80vw;
  margin: 40px auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 6px 32px rgba(0, 0, 0, 0.1);
  padding: 40px 32px 32px 32px;
  font-family: 'Segoe UI', 'Arial', sans-serif;
  color: #222;
  line-height: 1.7;
  .intro-title {
    font-size: 2.2rem;
    font-weight: 700;
    color: #000d65;
    margin-bottom: 0.5em;
    text-align: center;
  }
  .intro-verse {
    font-style: italic;
    color: #4a4a4a;
    background: #f7f5ff;
    border-left: 4px solid #000d65;
    padding: 12px 18px;
    margin: 24px 0 24px 0;
    font-size: 1.1rem;
  }
  .intro-section {
    margin-bottom: 1.5em;
  }
  .requirements {
    background: #f9f9f9;
    border-radius: 8px;
    padding: 18px 20px;
    margin: 24px 0;
    border-left: 4px solid #000d65;
  }
  .seminar-info {
    background: #f7f5ff;
    border-radius: 8px;
    padding: 18px 20px;
    margin: 24px 0;
    border-left: 4px solid #000d65;
    color: #333;
  }
  .form-section {
    margin-top: 40px;
    padding-top: 32px;
    border-top: 1px solid #eee;
    text-align: center;
  }
  .form-title {
    text-align: center;
    color: #000d65;
    margin-bottom: 24px;
    font-size: 2rem;
    font-weight: 600;
    letter-spacing: -1px;
  }
  ul.requirements-list {
    margin-top: 10px;
    margin-bottom: 0;
    padding-left: 20px;
  }
  .verse-ref {
    float: right;
    font-weight: 600;
    color: #000d65;
    font-size: 1rem;
  }
  .extra-info-container {
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin: 12px 0;
    align-items: stretch;
    justify-content: center;
    p {
      margin: 0 0 -3px 0;
    }
    .leader-info {
      display: flex;
      flex-direction: row;
      gap: 18px;
      margin: 12px 0 0 0;
      align-items: stretch;
      justify-content: center;
    }
    .leader-info-input {
      width: 100%;
      font-size: 1rem;
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 0;
    }
  }
  @media (max-width: ${screenSizes.tablet}) {
    max-width: 95vw;
    padding: 20px 16px 16px 16px;
    .form-title {
      font-size: 1.3rem;
    }
    .intro-title {
      font-size: 1.5rem;
    }
  }
`;

const RelationsInitialForm = (props) => {
  const { setStep, inputs, setInputs, onGuestUpdated } = props;
  const [errorInputs, setErrorInputs] = useState({});

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
    setErrorInputs((prev) => ({ ...prev, [name]: false }));
  };

  const selectHandle = (a, { value, name }) => handleInputs({ target: { value, name } });

  const verifyForm = () => {
    let errorInputs = {};
    let mandatoryInputs = [
      'name',
      'lastName',
      'identification',
      'phone',
      'email',
      'gender',
      'birthdate',
      'leaderName',
      'leaderPhone',
      'tienes_lider',
      'encuentro',
      'veces_seminario',
      'isLeader',
      'civil',
    ];

    if (inputs?.tienes_lider === 'Si') {
      mandatoryInputs = [...mandatoryInputs, 'leaderName', 'leaderPhone'];
    } else if (inputs?.tienes_lider === 'No') {
      mandatoryInputs = [...mandatoryInputs, 'Not_Complaint_Relaciones', 'Not_Complaint'];
    }
    // Waitlist users already completed the seminary; allow them to continue
    if (inputs?.encuentro === 'No' || (!inputs?.waitlist && inputs?.veces_seminario >= 1)) {
      mandatoryInputs = [...mandatoryInputs, 'Not_Complaint_Relaciones', 'Not_Complaint'];
    }

    mandatoryInputs.forEach((input) => {
      errorInputs = errorInput({
        errorInputs,
        input: { name: input, value: inputs[input] },
      });
    });

    setErrorInputs(errorInputs);
    return Object.values(errorInputs).includes(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (verifyForm()) return;
    setStep?.('form');
  };

  const leaderInfo = () => (
    <div className="extra-info-container">
      <div>
        <p>¿Eres líder de grupo de Discipulado?</p>
        {formSelectService({
          Select,
          handler: selectHandle,
          label: '¿Eres líder de grupo de discipulado?',
          name: 'isLeader',
          value: inputs.isLeader || '',
          errorInputs,
          options: ['Sí', 'No'],
        })}
      </div>
      <div>
        {formLabel(`encuentro`, '¿Has asistido a un encuentro?')}
        {formSelectService({
          Select,
          handler: selectHandle,
          label: '¿Has asistido a un encuentro?',
          name: 'encuentro',
          value: inputs.encuentro || '',
          errorInputs,
          options: ['Si', 'No'],
        })}
      </div>
      <div>
        {formLabel(`veces_seminario`, '¿Cuantas veces has asistido al seminario?')}
        {formInput({
          Input,
          handler: handleInputs,
          label: '¿Cuantas veces has asistido al seminario?',
          name: 'veces_seminario',
          value: inputs.veces_seminario,
          errorInputs,
          type: 'number',
        })}
      </div>
      <div>
        {formLabel(`tienes_lider`, '¿Tienes líder?')}
        {formSelectService({
          Select,
          handler: selectHandle,
          label: '¿Tienes líder?',
          name: 'tienes_lider',
          value: inputs.tienes_lider || '',
          errorInputs,
          options: ['Si', 'No'],
        })}
        {inputs.tienes_lider === 'Si' && (
          <>
            {formInput({
              Input,
              handler: handleInputs,
              label: 'Nombre de tu líder',
              name: 'leaderName',
              value: inputs.leaderName,
              errorInputs,
            })}
            {formInput({
              Input,
              handler: handleInputs,
              label: 'Teléfono de tu líder',
              name: 'leaderPhone',
              value: inputs.leaderPhone,
              type: 'number',
              errorInputs,
            })}
          </>
        )}
      </div>
      {Object.values(errorInputs).includes(true) && (
        <Message negative>
          <Message.Header>
            <Icon name="exclamation circle" /> Campos requeridos
          </Message.Header>
          <p>
            Por favor completa todos los campos obligatorios y las condiciones del seminario antes
            de continuar.
          </p>
        </Message>
      )}
      {errorInputs?.Not_Complaint_Relaciones === true && (
        <Message negative>
          <Message.Header>
            <p>
              Para poder participar del seminario, debes de tener líder, haber realizado encuentro
              (paso 1 crecer) y no haber recibido el seminario más de 1 vez.
            </p>
          </Message.Header>
          <p>
            Si tienes dudas, por favor escríbenos:{' '}
            <a href={'emailto:relacionessanascomunidadmde@gmail.com'}>
              relacionessanascomunidadmde@gmail.com
            </a>
            .
          </p>
        </Message>
      )}
    </div>
  );

  return (
    <Container>
      <div className="intro-title">¡Saludos!</div>
      <div className="intro-section">
        Gracias por querer participar del <strong>Seminario Relaciones Sanas</strong>.<br />
        <br />
        Un corazón sano, sana; y una Comunidad saludable crece. Creemos como Comunidad en la
        importancia de tener corazones sanos, y juntos crear una cultura de vulnerabilidad que nos
        ayudará a disfrutar de una comunidad saludable y expuesta a la luz de Cristo.
      </div>
      <div className="intro-section">
        Este proceso tiene como propósito acompañar a voluntarios, líderes y futuros líderes a ser
        capacitados y a identificar las heridas más profundas del corazón que en algunos casos
        pueden evidenciarse en comportamientos emocionales, relacionales y/o sexuales incorrectos,
        guiándonos en un proceso de restauración profunda basado en la relación con Dios y con los
        demás, llevándolos a comprender el amor del Padre y a descubrir el poder que hay en
        establecer relaciones sanas. De igual manera a recibir capacitación y formación para ayudar
        a otros a vivir en libertad.
      </div>
      <div className="intro-section">
        <strong>Relaciones Sanas</strong> se compone de 12 sesiones con la siguiente metodología:
        primera parte: seminario/taller de 45 minutos, segunda parte: Grupos de Transparencia.
      </div>
      <div className="intro-section">
        <strong>Grupos de Transparencia:</strong> Son grupos de máximo 8 personas, 1 Facilitador y 1
        facilitador en entrenamiento. Los espacios son creados con la finalidad de propiciar un
        ambiente seguro donde cada participante puede exponer los lugares más profundos de su
        corazón y aprender a ser vulnerable con otras personas en cuanto a sus quebrantos, rendir
        cuentas sobre avances de su proceso y también poder tener una retroalimentación de cada tema
        tratado en el taller. Podríamos decir que son espacios de transparencia donde con amor y
        cuidado nos alentamos los unos a los otros a permanecer en libertad.
      </div>
      <div className="intro-section">
        En estos grupos de transparencia prima la confidencialidad y la seguridad, así como en todo
        este proceso somos intencionales en cuidar con delicadeza las partes profundas que quizás no
        se han expuesto tan fácilmente.
      </div>
      <div className="intro-verse">
        “Si vivimos en la luz, así como Dios está en la luz, entonces tenemos comunión unos con
        otros, y la sangre de Jesús, su Hijo, nos limpia de todo pecado.”
        <br />
        <span className="verse-ref">1 Juan 1-7</span>
      </div>
      <div className="intro-section">
        <strong>
          Es muy importante que antes de finalizar tu inscripción tengas en cuenta los requisitos y
          confirmes que cuentas con la disponibilidad para asistir al seminario. Los cupos son
          limitados, y si no vas a poder asistir, otras persona podría ocupar tu lugar.
        </strong>
      </div>
      <div className="intro-section">
        Muchas veces tenemos dificultad de exponer algunas luchas con las cuales hemos estado
        batallando solos, luchas con la pornografía, masturbación, homosexualismo etc, han sido
        piedra de tropiezo para muchas personas que quieren avanzar en su llamado, y ese es el
        propósito de Relaciones Sanas, acompañar estos procesos de sanidad; de igual manera hemos
        visto cómo algunas personas aunque no están luchando con quebrantos sexuales, tienen muchas
        dificultades relacionales; dependencias tóxicas, inestabilidad emocional, mentiras,
        inseguridades, engaños, e incapacidad para construir relaciones profundas y avanzar en el
        propósito de Dios para sus vidas; esto, al igual que las luchas sexuales dificultan nuestro
        crecimiento espiritual y el alcance de nuestro llamado.
      </div>
      <div className="requirements">
        <strong>
          Queremos recordarte que para participar en el seminario es necesario cumplir con los
          siguientes requisitos:
        </strong>
        <ul className="requirements-list">
          <li>Tener líder de un grupo de discipulado.</li>
          <li>Haber completado al menos el Paso 1 del proceso "Crecer" (Encuentro).</li>
          <li>
            {inputs?.waitlist
              ? 'Si ya participaste, puedes dejar tus datos en lista de espera; se dará prioridad a quienes aún no lo han realizado.'
              : 'No haber participado previamente en este seminario.'}
          </li>
        </ul>
        {inputs?.waitlist ? (
          <div>
            <strong>Estás en lista de espera.</strong> Daremos prioridad a las personas que aún no
            han realizado este seminario. Si, una vez asignados los cupos, quedan espacios
            disponibles, nos pondremos en contacto contigo para confirmar tu participación.
          </div>
        ) : (
          <div>
            Si ya has recibido el seminario en el pasado y deseas volver a participar, te invitamos
            a escribirnos a este correo:{' '}
            <a href="mailto:relacionessanascomunidadmde@gmail.com">
              relacionessanascomunidadmde@gmail.com
            </a>
            .
          </div>
        )}
      </div>
      <div className="seminar-info">
        <Icon name="calendar alternate outline" color="violet" />{' '}
        <strong>Inicio del seminario</strong>: 20 de agosto, día jueves a las 6:45 p.m - 8:30 p.m{' '}
        <strong>Presencial en Campus Medellín</strong> (Cra 44 a # 31 -250, San Diego).
        <br />
        Si tienes alguna otra inquietud escríbenos al WhatsApp{' '}
        <a href="https://wa.me/573233019723" target="_blank" rel="noopener noreferrer">
          323 3019723
        </a>
        .<br />
        <br />
        <Icon name="info circle" color="violet" /> La inscripción al seminario está sujeta a la
        disponibilidad de cupos. En los próximos días, recibirás un mensaje de confirmación a través
        de tu facilitador del seminario.
      </div>
      <div className="form-section">
        <div className="form-title">Datos del participante</div>
        <form onSubmit={handleSubmit}>
          <Form
            Attendant={inputs}
            handleInputs={handleInputs}
            errorInputs={errorInputs}
            showGender
            showBirthdate
            showCivil
            showIdentification
            showPhone
            showEmail
            saveTag="Continuar"
            sendInForm
            submit={handleSubmit}
            moreInfo={leaderInfo}
            setAttendant={setInputs}
            setErrorInputs={setErrorInputs}
            onGuestUpdated={onGuestUpdated}
          />
        </form>
      </div>
    </Container>
  );
};

export default RelationsInitialForm;
